import { useEffect, useLayoutEffect, useState, useRef } from 'react';

const HUB_TABS = [
  { id: 'pedalboard', label: 'My Pedalboard' },
  { id: 'wants', label: 'My Wants' },
];

// Geometry shared by the real (grey) buttons and the white copies inside the
// indicator, so the two layers can't drift out of alignment.
const TAB_LABEL_STYLE = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.6rem 0.22rem',
  fontWeight: 700,
  fontSize: '0.875rem',
  lineHeight: 1.1,
  letterSpacing: '-0.02em',
  fontFamily: 'Nunito, sans-serif',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box',
};

/**
 * Mobile compact bar (Pedalboard / My Wants toggle + right slot dropdown) and
 * desktop expanded header for MyPedalsHub.
 *
 * The gold indicator follows a swipe so the gesture visibly drives these two
 * tabs rather than just moving the page. Its position comes from the inherited
 * --swipe-pos custom property that useLocalSwipeViews writes on the surface
 * during a drag, NOT from a prop: the drag updates it every frame, and as a prop
 * it would re-render this whole subtree (which builds the toggle twice) that
 * often. Where --swipe-pos is absent (web, no gesture) it falls back to the
 * active tab's index.
 */
export default function MyPedalsMobileChromeHeader({
  activeTab,
  onTabChange,
  rightSlot,
  children,
}) {
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab, rightSlot]);

  // Fallback position for when no gesture is driving --swipe-pos (web, desktop,
  // or simply at rest before the first swipe).
  const activeIndex = Math.max(0, HUB_TABS.findIndex(({ id }) => id === activeTab));

  const renderMobileChromeBar = () => (
    <div className="my-pedals-compact-bar-inner">
      <div
        className="my-pedals-view-toggle"
        role="tablist"
        aria-label="My Pedals view"
        style={{
          '--swipe-rest-pos': activeIndex,
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          // Recessed track with a raised sliding pill, same treatment as the
          // offer-mode control on the edit form (Jason, 2026-10-20).
          backgroundColor: 'white',
          borderRadius: '9999px',
          padding: '4px',
          boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          flex: 1,
          minWidth: 0,
          maxWidth: rightSlot ? 'calc(100% - 96px)' : '100%',
          boxSizing: 'border-box',
        }}
      >
        {HUB_TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            className="my-pedals-view-toggle-btn"
            data-tip-anchor={id === 'wants' ? 'my-wants-tab' : undefined}
            onClick={() => onTabChange(id)}
            style={{
              ...TAB_LABEL_STYLE,
              borderRadius: '9999px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: '#6B7280',
            }}
          >
            {label}
          </button>
        ))}
        {/* The indicator is its own layer rather than a per-button background so
            it can sit between tabs mid-swipe. It carries white copies of both
            labels, shifted back by exactly as much as the indicator moves
            forward: the copies therefore stay pinned over the grey originals and
            whichever part the indicator covers reads as white. That replaces
            interpolating the text colour in JS on every frame, and it stays
            correct at any fractional position. */}
        <div className="my-pedals-view-toggle-pill" aria-hidden="true">
          <div
            className="my-pedals-view-toggle-pill-labels"
            style={{ width: `${HUB_TABS.length * 100}%` }}
          >
            {HUB_TABS.map(({ id, label }) => (
              <span key={id} style={TAB_LABEL_STYLE}>{label}</span>
            ))}
          </div>
        </div>
      </div>
      {rightSlot ? (
        <div className="my-pedals-compact-right-slot">
          {rightSlot}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="my-pedals-sticky-header">
      <style>{`
        /* --swipe-pos is written by useLocalSwipeViews during a gesture and
           inherited from the swipe surface; --swipe-rest-pos is the committed
           tab, used on web and between gestures. */
        .my-pedals-view-toggle-pill {
          position: absolute;
          top: 4px;
          bottom: 4px;
          left: 4px;
          width: calc((100% - 8px) / ${HUB_TABS.length});
          border-radius: 9999px;
          background-color: #C89F3C;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.35);
          overflow: hidden;
          pointer-events: none;
          transform: translateX(calc(var(--swipe-pos, var(--swipe-rest-pos, 0)) * 100%));
          transition: transform 0.2s;
        }
        .my-pedals-view-toggle-pill-labels {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          display: flex;
          align-items: center;
          color: #ffffff;
          /* Cancels the pill's travel. The row is ${HUB_TABS.length} slots wide,
             so one slot is ${(100 / HUB_TABS.length).toFixed(0)}% of it. */
          transform: translateX(calc(var(--swipe-pos, var(--swipe-rest-pos, 0)) * -${100 / HUB_TABS.length}%));
          transition: transform 0.2s;
        }
        /* Mid-gesture the position comes straight from the thumb, so easing it
           would make the pill lag behind the pane. */
        .swipe-dragging .my-pedals-view-toggle-pill,
        .swipe-dragging .my-pedals-view-toggle-pill-labels {
          transition: none;
        }
        .my-pedals-compact-bar-inner {
          max-width: 1100px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
        }
        .my-pedals-compact-right-slot {
          flex-shrink: 0;
          min-width: 0;
        }
        .my-pedals-compact-right-slot select {
          max-width: 88px;
        }
        .my-pedals-sticky-header .my-pedals-header-expanded {
          display: block;
        }
        .my-pedals-sticky-header .my-pedals-mobile-chrome-slot {
          display: none;
        }
        .my-pedals-sticky-header .my-pedals-compact-bar-mobile-fixed {
          display: none;
        }
        @media (min-width: 769px) {
          .my-pedals-sticky-header .my-pedals-compact-bar {
            display: none !important;
          }
        }
        @media (max-width: 768px) {
          .my-pedals-page-content {
            padding-top: 0 !important;
          }
          .my-pedals-sticky-header .my-pedals-header-expanded {
            display: none !important;
          }
          .my-pedals-sticky-header .my-pedals-mobile-chrome-slot {
            display: block !important;
            position: relative;
            flex-shrink: 0;
            background-color: #F7F3EB;
            overflow: hidden;
          }
          .my-pedals-sticky-header .my-pedals-compact-bar-inflow {
            display: block;
            padding: 1rem 1rem 1.5rem 1rem;
            background-color: #F7F3EB;
            box-sizing: border-box;
          }
          .my-pedals-sticky-header .my-pedals-compact-bar-inflow.is-suppressed {
            visibility: hidden;
            pointer-events: none;
          }
          .my-pedals-sticky-header .my-pedals-compact-bar-mobile-fixed {
            display: block;
          }
          .my-pedals-sticky-header .my-pedals-compact-bar {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 100;
            padding: 0.35rem 1rem 0.85rem 1rem;
            background-color: #F7F3EB;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            box-sizing: border-box;
            pointer-events: none;
            transform: translateY(-100%);
            transition: transform 0.2s ease;
          }
          .my-pedals-sticky-header .my-pedals-compact-bar.is-mobile-visible {
            display: block;
            transform: translateY(0);
            pointer-events: auto;
          }
          .my-pedals-sticky-header .my-pedals-view-toggle-btn {
            padding: 0.6rem 0.22rem !important;
            font-size: 0.875rem !important;
            line-height: 1.1 !important;
            letter-spacing: -0.02em !important;
          }
          .my-pedals-compact-right-slot select {
            font-size: 0.875rem !important;
            padding: 0.6rem 1.25rem 0.6rem 0.55rem !important;
          }
        }
      `}</style>

      <div
        ref={mobileChromeSlotRef}
        className="my-pedals-mobile-chrome-slot"
        style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
      >
        <div
          ref={mobileChromeInflowRef}
          className={['my-pedals-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
        >
          {renderMobileChromeBar()}
        </div>
      </div>

      <div
        className={[
          'my-pedals-compact-bar',
          showMobileChromeBar && 'is-mobile-visible',
        ].filter(Boolean).join(' ')}
      >
        <div className="my-pedals-compact-bar-mobile-fixed">
          {renderMobileChromeBar()}
        </div>
      </div>

      <div className="my-pedals-header-expanded">
        {children}
      </div>
    </div>
  );
}
